import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  CheckCircleOutlined,
  CustomerServiceOutlined,
  DeleteOutlined,
  TagsOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { Checkbox, Tag } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useQuery } from '@tanstack/react-query';

import type { CommissionOverrideInput, PosSaleLine } from '../types';
import { useOptionalPosCart } from '../contexts/PosCartContext';
import { deriveIsPrepaid, hasPendingProfessional } from '../utils/serviceStatus';
import { resolveLineKey } from '../utils/lineKeys';

import './SaleLineRow.scss';

// import { LineNotesEditor } from './LineNotesEditor';
import { ServiceProfessionalPicker } from './ServiceProfessionalPicker';
import { PosQuantityStepper } from './PosQuantityStepper';
import { computeLineAmounts } from './discountMath';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { describeManualDiscount } from '../utils/discountPresentation';
import { useServiceStaffAssignments } from '@erp/services/api/serviceStaff';
// Importaciones actualizadas: módulo movido a features/erp/subscriptions
import { subscriptionsApi, SubscriptionBadge } from '@features/erp/subscriptions';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteApps } from '@store/slices/settingsSlice.tsx';

interface SaleLineRowProps {
  line: PosSaleLine;
  promotionAdjustment?: { amount: number; label: string };
  onQuantityChange?: (lineKey: string, quantity: number) => void;
  onRemoveLine?: (lineKey: string) => void;
  onProfessionalChange?: (lineKey: string, professionalId: number | null) => void;
  onNotesChange?: (lineKey: string, notes: string) => void;
  quantityStep?: number;
  canOverrideCommission?: boolean;
  showCommission?: boolean;
  subscriptionsEnabled?: boolean;
  onCommissionOverride?: (itemId: number, override: CommissionOverrideInput) => void;
  onEditDiscount?: (itemId: number) => void;
  customerId?: number | null;
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  backgroundColor: '#f0f5ff',
  color: '#1d39c4',
  borderRadius: '999px',
  padding: '0.15rem 0.5rem',
  fontSize: '0.7rem',
  fontWeight: 500,
};

export const SaleLineRow: React.FC<SaleLineRowProps> = ({
  line,
  promotionAdjustment,
  onQuantityChange,
  onRemoveLine,
  onProfessionalChange,
  // onNotesChange,
  quantityStep = 1,
  canOverrideCommission = false,
  showCommission: showCommissionProp,
  subscriptionsEnabled: subscriptionsEnabledProp,
  onCommissionOverride,
  onEditDiscount,
  customerId,
}) => {
  const lineKey = useMemo(() => resolveLineKey(line), [line]);
  const cartContext = useOptionalPosCart();
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const showCommission = showCommissionProp ?? siteApps.includes('erp__rrhh');
  const subscriptionsEnabled = subscriptionsEnabledProp ?? siteApps.includes('erp__subscriptions');

  const { data: availability } = useQuery({
    queryKey: ['subscription-availability', customerId, line.item.id],
    queryFn: () => subscriptionsApi.getAvailabilityForItem(customerId as number, line.item.id),
    enabled: subscriptionsEnabled && Boolean(customerId),
    staleTime: 60_000,
  });

  const unitPrice = subscriptionsEnabled && line.useSubscription ? 0 : line.item.price?.value ?? 0;
  const currency = line.item.price?.currency;
  const lineAmounts = computeLineAmounts(
    promotionAdjustment ? { ...line, manualDiscount: undefined } : line,
  );
  const manualDiscountAmount = promotionAdjustment ? 0 : lineAmounts.manualDiscountAmount;
  const promotionDiscountAmount = promotionAdjustment
    ? Math.min(promotionAdjustment.amount, lineAmounts.baseSubtotal)
    : 0;
  const displayedDiscountAmount = promotionDiscountAmount || manualDiscountAmount;
  const displayedSubtotal = Math.max(lineAmounts.baseSubtotal - displayedDiscountAmount, 0);
  const discountLabel = line.manualDiscount
    ? describeManualDiscount(line.manualDiscount, currency)
    : null;
  const isPrepaid = deriveIsPrepaid(line);
  const pendingProfessional = hasPendingProfessional(line);
  const canUseSubscription = Boolean(
    availability && (availability.unlimited || (availability.available ?? 0) >= line.quantity),
  );

  // Ref para evitar loops infinitos al actualizar el contexto
  const lastAvailabilityRef = useRef<typeof availability | null>(null);
  const hasAutoEnabledRef = useRef(false);

  useEffect(() => {
    if (!subscriptionsEnabled) {
      if (line.useSubscription) {
        cartContext?.updateSubscriptionUsage?.(lineKey, {
          useSubscription: false,
          subscriptionId: null,
          subscriptionAvailable: null,
          subscriptionUnlimited: false,
          subscriptionName: null,
        });
      }
      return;
    }

    // Solo actualizar si availability cambió (comparando subscription_id)
    const prevSubId = lastAvailabilityRef.current?.subscription_id;
    const currSubId = availability?.subscription_id;

    if (availability && prevSubId !== currSubId) {
      lastAvailabilityRef.current = availability;
      cartContext?.updateSubscriptionUsage?.(lineKey, {
        subscriptionId: availability.subscription_id,
        subscriptionAvailable: availability.available ?? null,
        subscriptionUnlimited: availability.unlimited,
        subscriptionName: availability.subscription_name ?? null,
      });

      // Auto-habilitar useSubscription la primera vez si hay disponibilidad
      if (!hasAutoEnabledRef.current && (availability.unlimited || (availability.available ?? 0) >= line.quantity)) {
        hasAutoEnabledRef.current = true;
        cartContext?.updateSubscriptionUsage?.(lineKey, { useSubscription: true });
      }
    }

    // Deshabilitar si ya no hay créditos suficientes
    if (line.useSubscription && !canUseSubscription) {
      cartContext?.updateSubscriptionUsage?.(lineKey, { useSubscription: false });
    }
  }, [
    availability,
    availability?.subscription_id,
    canUseSubscription,
    line.quantity,
    line.useSubscription,
    lineKey,
    cartContext,
    subscriptionsEnabled,
  ]);

  const handleRemove = useCallback(() => {
    if (onRemoveLine) {
      onRemoveLine(lineKey);
    } else {
      cartContext?.removeLine(lineKey);
    }
  }, [cartContext, lineKey, onRemoveLine]);

  // const handleNotesChange = useCallback(
  //   (nextValue: string) => {
  //     if (onNotesChange) {
  //       onNotesChange(lineKey, nextValue);
  //     } else {
  //       cartContext?.updateNotes(lineKey, nextValue);
  //     }
  //   },
  //   [cartContext, lineKey, onNotesChange],
  // );

  const handleProfessionalChange = useCallback(
    (nextProfessional: number | null) => {
      if (onProfessionalChange) {
        onProfessionalChange(lineKey, nextProfessional);
      } else {
        cartContext?.updateProfessional(lineKey, nextProfessional);
      }
    },
    [cartContext, lineKey, onProfessionalChange],
  );

  const handleSubscriptionToggle = useCallback(
    (event: CheckboxChangeEvent) => {
      if (!cartContext) {
        return;
      }
      const checked = event.target.checked;
      if (checked && availability && canUseSubscription) {
        cartContext.updateSubscriptionUsage(lineKey, {
          useSubscription: true,
          subscriptionId: availability.subscription_id,
          subscriptionAvailable: availability.available ?? null,
          subscriptionUnlimited: availability.unlimited,
          subscriptionName: availability.subscription_name ?? null,
        });
      } else {
        cartContext.updateSubscriptionUsage(lineKey, {
          useSubscription: false,
          subscriptionId: null,
          subscriptionAvailable: null,
          subscriptionUnlimited: false,
          subscriptionName: null,
        });
      }
    },
    [availability, canUseSubscription, cartContext, lineKey],
  );

  const handleCommissionReset = useCallback(() => {
    if (!onCommissionOverride) {
      return;
    }
    onCommissionOverride(line.item.id, {
      mode: 'reset',
      reason: 'reset',
    });
  }, [line.item.id, onCommissionOverride]);

  const handleCommissionRemove = useCallback(() => {
    if (!onCommissionOverride) {
      return;
    }
    onCommissionOverride(line.item.id, {
      mode: 'none',
      reason: 'no_commission',
    });
  }, [line.item.id, onCommissionOverride]);

  const showProfessionalPicker = line.item.item_type === 'service';
  const requiresAppointment = Boolean(
    line.flags?.requiresAppointment ?? line.item.service_detail?.is_appointment_based,
  );

  // Cargar profesionales asociados al servicio si es necesario
  const { data: staffAssignments } = useServiceStaffAssignments(
    showProfessionalPicker ? { serviceId: line.item.id } : undefined,
    { enabled: showProfessionalPicker }
  );

  // Usar profesionales del item si están disponibles, sino usar los cargados por el hook
  const professionals = useMemo(() => {
    if (line.item.professionals && line.item.professionals.length > 0) {
      return line.item.professionals;
    }

    if (staffAssignments && staffAssignments.length > 0) {
      return staffAssignments
        .filter((assignment) => assignment.staff)
        .map((assignment) => ({
          id: assignment.staff_user_id,
          full_name: assignment.staff?.first_name && assignment.staff?.last_name
            ? `${assignment.staff.first_name} ${assignment.staff.last_name}`
            : assignment.staff?.username ?? `Profesional ${assignment.staff_user_id}`,
          is_active: assignment.staff?.is_active ?? true,
        }));
    }

    return [];
  }, [line.item.professionals, staffAssignments]);

  // Seleccionar automáticamente si hay un único profesional activo
  useEffect(() => {
    // Solo si es un servicio con turno y no hay profesional asignado
    if (!showProfessionalPicker || line.professionalId != null) {
      return;
    }

    // Filtrar solo profesionales activos
    const activeProfessionals = professionals.filter((p) => p.is_active !== false);

    // Si hay exactamente un profesional activo, asignarlo automáticamente
    if (activeProfessionals.length === 1) {
      handleProfessionalChange(activeProfessionals[0].id);
    }
  }, [professionals, line.professionalId, showProfessionalPicker, handleProfessionalChange]);

  // const hasNotes = line.notes && line.notes.trim().length > 0;

  return (
    <article
      role="group"
      aria-label={`Línea de ${line.item.name}`}
      className="sale-line-row"
    >
      {/* Fila principal: miniatura, información, cantidad y subtotal */}
      <div className="sale-line-row__main">
        {/* Contenedor de Miniatura / Icono */}
        <div className="sale-line-row__thumb-wrapper">
          {line.item.thumbnail_url ? (
            <img
              src={line.item.thumbnail_url}
              alt={line.item.name}
              className="sale-line-row__thumb"
            />
          ) : line.item.item_type === 'service' ? (
            <CustomerServiceOutlined
              style={{ fontSize: '1.1rem', color: '#1890ff' }}
            />
          ) : (
            <ShoppingOutlined
              style={{ fontSize: '1.1rem', color: '#52c41a' }}
            />
          )}
        </div>

        {/* Columna de info */}
        <div className="sale-line-row__info">
          <div className="sale-line-row__title-row">
            <h3 className="sale-line-row__name">{line.item.name}</h3>
          </div>
        </div>

        <div className="sale-line-row__meta">
          <span className="sale-line-row__meta-code">
            <span
              className="sale-line-row__type-icon"
              aria-label={
                line.item.item_type === 'service' ? 'Servicio' : 'Producto'
              }
              title={
                line.item.item_type === 'service' ? 'Servicio' : 'Producto'
              }
            >
              {line.item.item_type === 'service' ? (
                <CustomerServiceOutlined />
              ) : (
                <ShoppingOutlined />
              )}
            </span>
            {line.item.sku ? (
              <span className="sale-line-row__sku">{line.item.sku}</span>
            ) : null}
          </span>
          <span className="sale-line-row__unit-price">
            {getFormattedPrice(unitPrice, currency)} c/u
          </span>
          {canUseSubscription ? (
            <Checkbox
              className="sale-line-row__subscription-inline"
              checked={Boolean(line.useSubscription)}
              onChange={handleSubscriptionToggle}
            >
              Usar suscripción
            </Checkbox>
          ) : null}
          {promotionAdjustment ? (
            <span className="sale-line-row__discount" aria-label="Promoción aplicada">
              <TagsOutlined /> {promotionAdjustment.label}
            </span>
          ) : discountLabel && onEditDiscount ? (
            <button
              type="button"
              className="sale-line-row__discount"
              onClick={() => onEditDiscount(line.item.id)}
              aria-label={`Editar descuento de ${line.item.name}`}
            >
              <TagsOutlined /> {discountLabel}
            </button>
          ) : discountLabel ? (
            <span
              className="sale-line-row__discount"
              aria-label="Descuento aplicado"
            >
              <TagsOutlined /> {discountLabel}
            </span>
          ) : null}
          {isPrepaid ? <span style={badgeStyle}>Prepago</span> : null}
          {pendingProfessional ? (
            <span
              style={{
                ...badgeStyle,
                backgroundColor: '#fff1f0',
                color: '#d4380d',
              }}
            >
              Prof. pendiente
            </span>
          ) : null}
          {line.flags?.hasPromotion ? (
            <span
              style={{
                ...badgeStyle,
                backgroundColor: '#fff7e6',
                color: '#d46b08',
              }}
            >
              Promo
            </span>
          ) : null}
        </div>

        {/* Controles de cantidad */}
        <PosQuantityStepper
          quantity={line.quantity}
          itemName={line.item.name}
          step={quantityStep}
          onChange={(quantity) => {
            if (onQuantityChange) onQuantityChange(lineKey, quantity);
            else cartContext?.updateQuantity(lineKey, quantity);
          }}
          onRemove={handleRemove}
          className="sale-line-row__quantity"
        />

        {/* Precios */}
        <div className="sale-line-row__prices">
          {displayedDiscountAmount > 0 ? (
            <div className="sale-line-row__original-price">
              {getFormattedPrice(
                lineAmounts.baseSubtotal,
                currency
              )}
            </div>
          ) : null}
          <div
            className={`sale-line-row__total-price${displayedDiscountAmount > 0 ? ' has-discount' : ''}`}
          >
            {getFormattedPrice(displayedSubtotal, currency)}
          </div>
        </div>

        <button
          type="button"
          onClick={handleRemove}
          className="sale-line-row__remove-btn"
          aria-label={`Quitar ${line.item.name}`}
        >
          <DeleteOutlined />
        </button>
      </div>

      {/* Los hijos ya llegan como líneas vendibles independientes. Sólo los
          servicios necesitan configuración adicional en el carrito. */}
      {showProfessionalPicker ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '0.5rem',
          }}
        >
          <ServiceProfessionalPicker
            professionals={professionals.map((professional) => ({
              id: professional.id,
              name: professional.full_name ?? `Profesional ${professional.id}`,
              isActive: professional.is_active,
            }))}
            value={line.professionalId ?? null}
            onChange={handleProfessionalChange}
            allowPending={!requiresAppointment}
            pendingLabel="Seleccionar profesional"
          />
        </div>
      ) : null}

      {false ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            <Checkbox
              checked={Boolean(line.useSubscription)}
              disabled={!canUseSubscription}
              onChange={handleSubscriptionToggle}
            >
              Usar suscripción
            </Checkbox>
            {availability != null ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                {(availability as NonNullable<typeof availability>).unlimited
                  ? 'Ilimitado'
                  : `${Math.floor((availability as NonNullable<typeof availability>).available ?? 0)} créditos`}
              </Tag>
            ) : (
              <SubscriptionBadge
                customerId={customerId ?? undefined}
                itemId={line.item.id}
                queryEnabled={!availability}
              />
            )}
          </div>
          {line.useSubscription ? (
            <Tag
              color="success"
              icon={<CheckCircleOutlined />}
              style={{ marginLeft: 'auto' }}
            >
              Suscripción aplicada
            </Tag>
          ) : null}
        </div>
      ) : null}

      {/* Comisión y acciones (si aplica) */}
      {showCommission && line.commission?.agreementId ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.8rem',
            color: '#595959',
            paddingTop: '0.25rem',
            borderTop: '1px solid #f0f0f055',
          }}
        >
          <span>
            Comisión:{' '}
            {getFormattedPrice(line.commission?.amount ?? 0, currency)}
          </span>
          {canOverrideCommission && onCommissionOverride ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={handleCommissionReset}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2f54eb',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Recalcular
              </button>
              <button
                type="button"
                onClick={handleCommissionRemove}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d4380d',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Sin comisión
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*/!* Editor de notas *!/*/}
      {/*<LineNotesEditor*/}
      {/*  id={`${lineKey}-notes`}*/}
      {/*  value={line.notes ?? ''}*/}
      {/*  onChange={handleNotesChange}*/}
      {/*/>*/}
    </article>
  );
};

export default SaleLineRow;
